import React, { ReactElement, useState } from 'react';

import { Box } from '@chakra-ui/react';
import Underline from 'images/highlights/line-3-cross.svg';
import Highlight from 'images/highlights/yellow-simple.png';
import { GetStaticProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';

import { StaticBackground } from '@/components/atoms/StaticBackground';
import { TextDecorationPng, TextDecorationSvg } from '@/components/functional';
import { DefaultLayout, Footer } from '@/components/layouts';
import { RecommendedArticlesSection } from '@/components/sections/common/RecommendedArticlesSection';
import { ContactPersonSection } from '@/components/sections/services/sections';
import { PropsBuilder } from '@/graphql/api';
import { useGetGlobalQuery } from '@/graphql/generated';
import { NextPageWithLayout } from '@/pages/_app';
import { DEFAULT_LOCALE, LOCALES, PAGE_REVALIDATE_TIME } from '@/utils/constants';

const CalendlyModalNoSSR = dynamic(() => import('../../components/molecules/CalendlyModal/CalendlyModal'), {
  ssr: false,
});

export const ThanksPage: NextPageWithLayout = () => {
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const { t } = useTranslation(LOCALES.THANKS);
  const isDevelopment = process.env.NODE_ENV === 'development';

  const router = useRouter();

  const isPlOverride = router.query?.version === 'pl';
  const primedT = (key: string) => t(`${isPlOverride ? 'plOverride.' : ''}${key}`);

  return (
    <>
      {!isDevelopment && (
        <Script id="pixel-fb-thanks">
          {`
            fbq('track', 'Lead');
          `}
        </Script>
      )}
      <StaticBackground maxW="800px" maxH="800px" w="100%" h="100%" left="0" top="10%" />
      <Box
        mb={{
          base: -20,
        }}
      >
        <ContactPersonSection
          header={
            <>
              {primedT('header.0')}{' '}
              <TextDecorationPng image={Highlight} once={true}>
                {primedT('header.1')}
              </TextDecorationPng>
            </>
          }
          paragraphs={[primedT('paragraph')]}
          flip={true}
          imageStyle={{
            w: {
              base: '221px',
              sm: '270px',
              md: '604px',
            },
          }}
          buttonAction={() => setCalendlyOpen(true)}
          buttonText={primedT('btnTitle')}
        />
      </Box>
      {!isPlOverride && (
        <RecommendedArticlesSection
          tags={[]}
          slug={''}
          header={
            <>
              {t('recommendedArticles.text')}{' '}
              <TextDecorationSvg
                once={true}
                variant="line"
                color="violet"
                line={Underline}
                top="54%"
                strokeWidth={2.5}
                delay="2s"
              >
                {t('recommendedArticles.underline')}
              </TextDecorationSvg>
            </>
          }
        />
      )}
      <CalendlyModalNoSSR open={calendlyOpen} onClose={() => setCalendlyOpen(false)} />
    </>
  );
};

ThanksPage.getLayout = (page: ReactElement) => {
  const { metadata } = page.props;

  return (
    <DefaultLayout metadata={metadata}>
      {page}
      <Footer />
    </DefaultLayout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const propsBuilder = new PropsBuilder();

  propsBuilder.withQuery(useGetGlobalQuery, {
    variables: {
      locale,
    },
  });

  const lang = await serverSideTranslations(locale || DEFAULT_LOCALE, [LOCALES.COMMON, LOCALES.THANKS]);

  return {
    props: {
      ...(await propsBuilder.build(lang)),
    },
    revalidate: PAGE_REVALIDATE_TIME,
  };
};

export default ThanksPage;
